import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  Host,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { filter, fromEvent, Subject, Subscription, takeUntil } from 'rxjs';

@Directive({
  selector: 'ng-template[floatingMenuContent]',
})
export class ContentTemplate {
  constructor(public templateRef: TemplateRef<any>) {}
}

@Component({
  selector: 'app-floating-menu',
  templateUrl: 'floating-menu.component.html',
  styleUrls: ['floating-menu.component.scss'],
  animations: [
    trigger('handleOpened', [
      state(
        'true',
        style({
          transform: 'translateX(0) translateY(-50%)',
        })
      ),
      state(
        'false',
        style({
          transform: 'translateX(100%) translateY(-50%)',
        })
      ),
      transition(
        'true <=> false',
        animate('300ms cubic-bezier(0.49,-0.14, 0.48, 1.22)')
      ),
    ]),
  ],
})
export class FloatingMenuComponent implements OnInit, OnChanges, OnDestroy {
  @ContentChild(ContentTemplate, { static: false })
  contentTpl: ContentTemplate;

  @ViewChild('btnOpenEl', { static: true })
  btnOpenElRef: ElementRef<HTMLDivElement>;
  @ViewChild('menuContainerEl', { static: true })
  menuContainerElRef: ElementRef<HTMLDivElement>;

  @Input()
  opened = false;
  @Output()
  openedChange = new EventEmitter<boolean>();

  @Input()
  fixed = false;
  @Output()
  fixedChange = new EventEmitter<boolean>();

  @Output()
  onOpening = new EventEmitter<any>();
  @Output()
  onOpened = new EventEmitter<any>();
  @Output()
  onClosing = new EventEmitter<any>();
  @Output()
  onClosed = new EventEmitter<any>();

  @Input()
  openTrigger: 'click' | 'mouseover' = 'click';

  @Input()
  closeOnMouseleave = false;

  initialized = false;

  openedState = false;

  openedInternal = false;
  opening = false;
  closing = false;
  closed = true;

  @Input()
  container: HTMLElement;

  @Input()
  contentHeight: string;

  @Input()
  closedIndicatorHeight: string;

  private openTriggerSubscription: Subscription;
  private closeOnMouseleaveSubscription: Subscription;

  private lifecycle = new Subject<void>();

  constructor(
    @Host()
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.openedInternal = this.opened;
    this.closed = !this.opened;

    this.handleOpenTriggerEvent();
    this.handleCloseOnMouseleave();

    this.initialized = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.opened) {
      this.openedState = this.opened;
    }

    if (changes.openTrigger && !changes.openTrigger.firstChange) {
      this.handleOpenTriggerEvent();
    }

    if (changes.closeOnMouseleave && !changes.closeOnMouseleave.firstChange) {
      this.handleCloseOnMouseleave();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onTriggerEventStart(e) {
    if (!this.initialized) {
      return;
    }

    this.opening = e.fromState === false && e.toState === true;
    this.closing = !this.opening;

    this.openedInternal = false;
    this.closed = false;

    this.syncHostStateClass();
    this.changeDetector.detectChanges();

    if (this.opening) {
      this.onOpening.emit();
    } else if (this.closing) {
      this.onClosing.emit();
    }
  }

  onTriggerEventDone(e) {
    if (!this.opening && !this.closing) {
      return;
    }

    if (
      (this.opening && !this.openedState) ||
      (this.closing && this.openedState)
    ) {
      return;
    }

    this.openedInternal = this.opening;

    const shouldEmitOpenedChanges = this.openedInternal !== this.opened;

    this.opened = this.openedInternal;
    this.closed = !this.openedInternal;

    if (this.opening) {
      this.opening = false;
      this.syncHostStateClass();
      this.changeDetector.detectChanges();

      if (shouldEmitOpenedChanges) {
        this.openedChange.emit(this.opened);
      }

      this.onOpened.emit();
    } else if (this.closing) {
      this.closing = false;
      this.syncHostStateClass();
      this.changeDetector.detectChanges();

      if (shouldEmitOpenedChanges) {
        this.openedChange.emit(this.opened);
      }

      this.onClosed.emit();
    }
  }

  onBtnOpenClick() {
    if (this.fixed) {
      return;
    }

    this.openedState = true;
  }

  private handleOpenTriggerEvent() {
    this.openTriggerSubscription?.unsubscribe();

    const eventName = this.openTrigger === 'mouseover' ? 'mouseover' : 'click';
    this.openTriggerSubscription = fromEvent(
      this.btnOpenElRef.nativeElement,
      eventName
    )
      .pipe(
        filter(() => !this.fixed),
        takeUntil(this.lifecycle)
      )
      .subscribe(() => {
        this.openedState = true;
      });
  }

  private handleCloseOnMouseleave() {
    this.closeOnMouseleaveSubscription?.unsubscribe();

    if (!this.closeOnMouseleave) {
      return;
    }

    this.closeOnMouseleaveSubscription = fromEvent(
      this.menuContainerElRef.nativeElement,
      'mouseleave'
    )
      .pipe(
        filter(() => !this.fixed),
        takeUntil(this.lifecycle)
      )
      .subscribe(() => {
        this.openedState = false;
      });
  }

  private syncHostStateClass() {
    const aux: [boolean, string][] = [
      [this.openedInternal, 'state-opened'],
      [this.opening, 'state-opening'],
      [this.closing, 'state-closing'],
      [this.closed, 'state-closed'],
    ];

    aux.forEach(([value, className]) => {
      if (value) {
        this.renderer.addClass(this.elementRef.nativeElement, className);
      } else {
        this.renderer.removeClass(this.elementRef.nativeElement, className);
      }
    });
  }
}
